import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App.js";
import "./tokens/fonts.js";
import "./tokens/tokens.css";
import "./app.css";
import "./desktop.css";
import "./display.css";

const root = document.getElementById("root");
if (!root) throw new Error("No #root element — index.html is wrong.");

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/*
 * The service worker is what makes the home-screen icon open yesterday's
 * schedule instead of a dinosaur when the network is gone.
 *
 * The kitchen screen is registered too. The rule that surface lives by is that
 * it must never look confident and be wrong — but its payload carries
 * generated_at, so a cached answer arrives already labelled with its own age
 * and the screen degrades itself on schedule. Between this morning's schedule
 * under an honest stale banner and a Chromium error page on the kitchen wall,
 * the cache is the more honest of the two.
 */
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // No service worker is a degraded install, not a broken app.
    });
  });

  /*
   * The kitchen screen picks up a new build the moment one is ready.
   *
   * A rendered page keeps the assets it started with, so without this the wall
   * runs the previous build until its six-hourly reload — half a day of
   * looking at a fix that shipped this morning, with nothing on screen to say
   * so. There is nothing to lose to a reload there: no draft, no scroll
   * position, no signed-in anything.
   *
   * The phone deliberately does not do this. It picks up the new build on the
   * next open, and reloading out from under someone mid-sentence in the editor
   * would cost them the sentence.
   *
   * Guarded on there already being a controller: the very first visit takes
   * control after load, and reloading on that would be a loop.
   */
  /*
   * When a new build takes over, run it.
   *
   * A rendered page keeps the assets it started with, so a tab left open —
   * which is what a home-screen app is — can go on running last week's code
   * long after a fix shipped, with nothing on screen to say so. That is not a
   * theoretical problem: it is how a fixed bug gets reported twice.
   *
   * The one exception is someone mid-edit. Reloading out from under a
   * half-typed event costs them the event, and no deploy is worth that; they
   * pick the new build up on the next open instead.
   */
  if (navigator.serviceWorker.controller !== null) {
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      const editing =
        window.location.pathname.endsWith("/edit") ||
        window.location.pathname === "/event/new";
      if (editing) return;
      window.location.reload();
    });
  }

  if (window.location.pathname.startsWith("/display")) {

    /*
     * And it goes looking, rather than waiting to be told.
     *
     * A browser only re-checks sw.js on navigation or roughly once a day, so a
     * screen that reloads every six hours could sit a full day behind a
     * deploy. This is one conditional GET every half hour; when it turns up a
     * new build the listener above puts it on the wall within seconds.
     */
    setInterval(
      () => {
        void navigator.serviceWorker.getRegistration().then((registration) => {
          void registration?.update();
        });
      },
      30 * 60 * 1000,
    );
  }
}
